import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDays, Combine, Trash2 } from 'lucide-react'
import type { Project, StartPeriod } from '@/src/renderer/lib/db/models'
import type { TaskEntryData } from '../TaskEntry'
import { TaskDropdown } from '../TaskDropdown'
import { TodayIcon, TomorrowIcon, WhenDropdownContent } from '../WhenDropdown'
import { MoveProjectDropdown } from '@/src/renderer/features/taskManagement/pages/QuickNotesPage/ClarifyView/MoveProjectDropdown'
import { WakeInProjectIcon } from '@/src/renderer/components/icons/WakeInProjectIcon'
import type { StagedAction } from '../../utils/buildTaskUpdate'
import './ReactivateMenuList.scss'

export interface ReactivateMenuListProps {
  task: TaskEntryData
  onAction: (action: StagedAction) => void
  onClose: () => void
}

interface MenuItemProps {
  label: string
  icon: React.ReactNode
  shortcut?: string
  onClick: () => void
  destructive?: boolean
}

function MenuItem({ label, icon, shortcut, onClick, destructive }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`reactivate-menu__item${destructive ? ' reactivate-menu__item--destructive' : ''}`}
      onClick={onClick}
    >
      <span className="reactivate-menu__icon" aria-hidden="true">{icon}</span>
      <span className="reactivate-menu__label">{label}</span>
      {shortcut && <span className="reactivate-menu__shortcut">{shortcut}</span>}
    </button>
  )
}


/**
 * The list of actions shown when the user opens the Reactivate dropdown
 * on a dormant task. Mirrors the Clarify ActionPanel behaviour: simple
 * actions commit immediately via `onAction(...)`, and "Demnächst" /
 * "In Projekt verschieben" each open a nested `TaskDropdown` to gather
 * the missing payload before committing.
 *
 * The "In Projekt aufwecken" entry is rendered only when the dormant
 * task already has a project; in that case it just clears `pausedAt`
 * and the row leaves the Ruhend list.
 */
export function ReactivateMenuList({ task, onAction, onClose }: ReactivateMenuListProps) {
  const { t } = useTranslation()
  const [laterOpen, setLaterOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)

  const closeAll = useCallback(() => {
    setLaterOpen(false)
    setMoveOpen(false)
    onClose()
  }, [onClose])

  const handleWakeInProject = () => {
    if (!task.projectId || !task.projectName || !task.lifeSpace) return
    onAction({
      kind: 'wakeInProject',
      projectId: task.projectId,
      projectName: task.projectName,
      lifeSpace: task.lifeSpace,
    })
    closeAll()
  }
  const handleToday = () => {
    onAction({ kind: 'today' })
    closeAll()
  }
  const handleTomorrow = () => {
    onAction({ kind: 'tomorrow' })
    closeAll()
  }
  const handleDelete = () => {
    onAction({ kind: 'delete' })
    closeAll()
  }

  const handleLaterChange = (value: { date: Date; time: string | null; period: StartPeriod | null }) => {
    onAction({ kind: 'later', date: value.date, time: value.time, period: value.period })
    closeAll()
  }

  const handleProjectSelect = (project: Project) => {
    onAction({ kind: 'move', projectId: project.id, projectName: project.name })
    closeAll()
  }

  // Need projectName + lifeSpace too — they're populated only by pages
  // that wire the project lookup (currently DormantPage). Without them
  // the toast button label would be empty and the open-project handler
  // wouldn't know which path to navigate to.
  const showWakeInProject = task.projectId != null && task.projectName != null && task.lifeSpace != null

  return (
    <div className="reactivate-menu" role="menu">
      <div className="reactivate-menu__title" role="presentation">
        {t('dormant.menu.title')}
      </div>

      {showWakeInProject && (
        <>
          <MenuItem
            label={t('dormant.menu.wakeInProject')}
            icon={<WakeInProjectIcon />}
            onClick={handleWakeInProject}
          />
          <div className="reactivate-menu__separator" aria-hidden="true" />
        </>
      )}

      <MenuItem
        label={t('dormant.menu.today')}
        icon={<TodayIcon size={16} />}
        shortcut="⌘H"
        onClick={handleToday}
      />
      <MenuItem
        label={t('dormant.menu.tomorrow')}
        icon={<TomorrowIcon size={16} />}
        shortcut="⌘M"
        onClick={handleTomorrow}
      />

      <TaskDropdown
        open={laterOpen}
        onOpenChange={setLaterOpen}
        side="right"
        align="start"
        sideOffset={8}
        trigger={
          <button
            type="button"
            role="menuitem"
            className="reactivate-menu__item reactivate-menu__item--later"
          >
            <span className="reactivate-menu__icon" aria-hidden="true">
              <CalendarDays size={16} />
            </span>
            <span className="reactivate-menu__label">{t('dormant.menu.later')}</span>
            <span className="reactivate-menu__shortcut">⌘D</span>
          </button>
        }
      >
        <WhenDropdownContent
          value={null}
          onChange={handleLaterChange}
          onClose={() => setLaterOpen(false)}
          variant="actionbar"
        />
      </TaskDropdown>

      <TaskDropdown
        open={moveOpen}
        onOpenChange={setMoveOpen}
        side="right"
        align="start"
        sideOffset={8}
        trigger={
          <button
            type="button"
            role="menuitem"
            className="reactivate-menu__item reactivate-menu__item--move"
          >
            <span className="reactivate-menu__icon" aria-hidden="true">
              <Combine size={16} />
            </span>
            <span className="reactivate-menu__label">{t('dormant.menu.move')}</span>
            <span className="reactivate-menu__shortcut">⌘V</span>
          </button>
        }
      >
        <MoveProjectDropdown
          lifeSpace={task.lifeSpace ?? 'personal'}
          onSelect={handleProjectSelect}
        />
      </TaskDropdown>

      <div className="reactivate-menu__separator" aria-hidden="true" />

      <MenuItem
        label={t('dormant.menu.delete')}
        icon={<Trash2 size={16} />}
        shortcut="⌘L"
        onClick={handleDelete}
        destructive
      />
    </div>
  )
}
